<script>
    /**
     * SPDX-FileCopyrightText: 2012-2026 Open Assessment Technologies S.A.
     * Copyright (C) 2026 (original work) Open Assessment Technologies S.A.
     *
     * SPDX-License-Identifier: AGPL-3.0-only OR LicenseRef-TAO-Commercial-License
     */
    import { getTestStateStore, getTestSessionStatusStore } from '../../../testsStateStore.js';
    import { tick } from 'svelte';
    import { __ } from '@oat-sa-private/ui-core';

    /**
     * @typedef {Object} Props
     * @property {String} serviceCallId
     * @property {Array} [items]
     * @property {Function} onFocusElement
     * @property {Function} onHighlight
     * @property {Function} onUnhighlight
     *
     */
    /** @type {Props} */
    // prettier-ignore
    let {
        serviceCallId,
        items = [],
        onFocusElement,
        onHighlight,
        onUnhighlight
    } = $props();

    // Expose refresh trigger for when menu items changed between $testStateStore changes
    export function refresh() {
        setAvailableItems();
    }

    // svelte-ignore state_referenced_locally
    const testStateStore = getTestStateStore(serviceCallId);
    // svelte-ignore state_referenced_locally
    const testStatusStore = getTestSessionStatusStore(serviceCallId);

    let availableItems = $state([]);

    /**
     * Fires when menu item was pressed
     * @param {string} itemType - item type(e.g toolbox or navigation)
     */
    function handleJumpLinkAction(itemType) {
        onFocusElement?.({ itemType });
    }

    /**
     * Fires when menu item was focused
     * @param {string} itemType - item type(e.g toolbox or navigation)
     */
    function handleJumpLinkFocus(itemType) {
        onHighlight?.({ itemType });
    }

    /**
     * Fires when menu item was focused out
     * @param {string} itemType - item type(e.g toolbox or navigation)
     */
    function handleJumpLinkFocusOut(itemType) {
        onUnhighlight?.({ itemType });
    }

    /**
     * Filter items by status and availability.
     * If item has specific selectors should check that at least one of them is available.
     * For example for toolbox should be taken area and try to query by selector.
     * If there arent any selectors should be return true because registered area in areaBroker always available
     */
    function setAvailableItems() {
        availableItems = items.filter(item => {
            // filtering by status
            if (!item.availableStatuses.includes($testStatusStore)) {
                return false;
            }

            return item.getFocusableElement(item.area);
        });
    }
    let currentItem = $derived($testStateStore ? testStateStore.getCurrentItem() : null);
    let currentTestMap = $derived($testStateStore ? testStateStore.getTestMap() : null);
    let currentTestPart = $derived($testStateStore ? testStateStore.getCurrentTestPart() : null);

    // After $testStateStore was updated UI can be not updated => tick is necessity
    $effect(() => {
        $testStateStore ? tick().then(() => setAvailableItems()) : '';
    });
</script>

<style>
    .jump-menu {
        position: absolute;
        z-index: var(--layer-5);
        top: 0;
        left: 0;

        & ul {
            list-style: none;
            margin: 0;
            padding: 0;

            & li button {
                height: 0;
                display: block;
                padding: 0;
                color: var(--color-text-active);
                background: var(--color-brand);
                font-family: var(--font-ui);
                font-size: var(--fontsize-body);
                border: none;
                outline: none;
                pointer-events: none;
                opacity: 0;

                &:focus {
                    height: 9rem;
                    margin: 0.5rem;
                    padding: var(--space-1x) var(--space-5x);
                    opacity: 1;
                }
            }
        }
    }
</style>

<div role="region" class="jump-menu" aria-labelledby="a11y-jump-menu">
    <h2 id="a11y-jump-menu" class="visually-hidden">{__('Jump Menu')}</h2>
    <ul>
        {#each availableItems as item (item.type)}
            <li>
                <button
                    data-test-id="jump-{item.type}"
                    onclick={() => handleJumpLinkAction(item.type)}
                    onfocus={() => handleJumpLinkFocus(item.type)}
                    onfocusout={() => handleJumpLinkFocusOut(item.type)}>
                    {__('Jump to')}
                    <strong>{item.getLabel(currentItem, currentTestPart, currentTestMap)}</strong>
                </button>
            </li>
        {/each}
    </ul>
</div>
